// File: GroupBanUserDialog.tsx
import React, { useMemo, useState } from "react";
import { Localizer } from "@/main/react/Types";
import {
    AjaxGroupRestControllerApi,
    BanUserRequest,
    BanUserRequestDurationEnum,
    BanUserRequestReasonTypeEnum,
} from "@/generated-client";
import { ClientFactory } from "@/main/common/ClientFactory";
import { AlertTriangle, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import { ErrorBanner } from "@/main/react/component/ErrorBanner";
import { UsernameSuggestionField } from "@/main/react/component/UsernameSuggestionField";
import { LocalizationKey } from "@/main/util/LocalizationUtil";
import SimpleDialog, { openSimpleDialog, SimpleDialogFooter } from "@/main/react/Dialog/SimpleDialog";
import logger from "@/lib/logger";
import {validateModerationForm} from "@/main/validation/moderation";
import {REQUEST_VALIDATION_LIMITS} from "@/main/validation/request";
import {groupBanReasonLocalizationKey} from "@/main/common/GroupBanReasons";

type Props = {
    groupPublicId: string;
    onClose?: () => void;
    localizer: Localizer;
    targetUsername?: string;
    /**
     * Bulk mode (roadmap P2-C): ban every listed member with one reason/duration/type. There is no bulk
     * ban endpoint — each username goes through the same per-user `banUser` call and therefore the same
     * server-side authorization as a single ban. A partially-authorized selection partially succeeds,
     * and `onBulkResult` reports the split so the caller can say so rather than claim a clean sweep.
     */
    targetUsernames?: string[];
    onSuccess?: () => void;
    onBulkResult?: (result: { succeeded: string[]; failed: string[] }) => void;
};

type BanReasonType = BanUserRequest["reasonType"];
type BanDuration = BanUserRequest["duration"];

const DURATION_OPTIONS: { value: BanDuration; labelKey: LocalizationKey; labelFallback: string }[] = [
    { value: BanUserRequestDurationEnum.Permanent, labelKey: "groups.modal.ban.duration.permanent", labelFallback: "Permanent" },
    { value: BanUserRequestDurationEnum.OneDay, labelKey: "groups.modal.ban.duration.one_day", labelFallback: "1 day" },
    { value: BanUserRequestDurationEnum.ThreeDays, labelKey: "groups.modal.ban.duration.three_days", labelFallback: "3 days" },
    { value: BanUserRequestDurationEnum.OneWeek, labelKey: "groups.modal.ban.duration.one_week", labelFallback: "1 week" },
    { value: BanUserRequestDurationEnum.OneMonth, labelKey: "groups.modal.ban.duration.one_month", labelFallback: "1 month" },
];

const REASON_TYPES: { value: BanReasonType; label: string }[] = [
    { value: BanUserRequestReasonTypeEnum.Spam, label: "Spam" },
    { value: BanUserRequestReasonTypeEnum.Harassment, label: "Harassment" },
    { value: BanUserRequestReasonTypeEnum.HateSpeech, label: "Hate speech" },
    { value: BanUserRequestReasonTypeEnum.Abuse, label: "Abuse" },
    { value: BanUserRequestReasonTypeEnum.RuleViolation, label: "Rule violation" },
    { value: BanUserRequestReasonTypeEnum.Copyright, label: "Copyright" },
    { value: BanUserRequestReasonTypeEnum.AiSlop, label: "AI slop" },
    { value: BanUserRequestReasonTypeEnum.Other, label: "Other" },
];


export function GroupBanUserDialogInner({ groupPublicId, onClose, localizer, targetUsername, targetUsernames, onSuccess, onBulkResult }: Props) {
    const bulkTargets = (targetUsernames ?? []).filter((name) => Boolean(name && name.trim()));
    const isBulk = bulkTargets.length > 0;
    const [saving, setSaving] = useState(false);
    const [usernameStr, setUsernameStr] = useState(targetUsername ?? "");
    const [reasonType, setReasonType] = useState<BanReasonType>(REASON_TYPES[0].value);
    const [reason, setReason] = useState("");
    const [duration, setDuration] = useState<BanDuration>(DURATION_OPTIONS[0].value);
    const [error, setError] = useState<string | null>(null);
    // C4 of the 2026-08-28 moderation toolkit: ban-a-spammer is not complete while their messages
    // stay in the room. Single-target only; bulk bans already run sequentially and a purge per
    // target would multiply the writes the throttle exists to bound.
    const [purgeMessages, setPurgeMessages] = useState(false);

    const apiClient: AjaxGroupRestControllerApi = useMemo(
        () => ClientFactory.getAjaxGroupRestControllerApi(),
        []
    );

    async function handleSubmit(e?: React.FormEvent) {
        e?.preventDefault();
        setError(null);

        const trimmedUsername = usernameStr.trim();
        const trimmedReason = reason.trim();
        const validationMessage = validateModerationForm(
            {
                // In bulk mode the targets come from the selection, not a field, so validate the first
                // of them purely to keep the shared reason-length/format checks in one place.
                username: isBulk ? bulkTargets[0] : trimmedUsername,
                reason: trimmedReason,
            },
            {
                invalidUsername: localizer("groups.modal.ban.invalid_username"),
                requiredReason: localizer("groups.modal.ban.require_reason"),
                reasonTooLong: `Reason must be at most ${REQUEST_VALIDATION_LIMITS.longText.maxLength} characters.`,
            }
        );
        if (validationMessage) {
            setError(validationMessage);
            return;
        }
        setSaving(true);
        try {
            const buildPayload = (username: string): BanUserRequest => ({
                username,
                reason: trimmedReason,
                reasonType,
                duration,
            });

            if (isBulk) {
                // Sequential on purpose: each ban is a moderation write, and a burst of parallel writes
                // against the same group is exactly what the endpoint throttle is there to stop.
                const succeeded: string[] = [];
                const failed: string[] = [];
                for (const target of bulkTargets) {
                    try {
                        await apiClient.banUser(groupPublicId, buildPayload(target));
                        succeeded.push(target);
                    } catch (err) {
                        logger.error("Failed banning user in bulk", { err, target });
                        failed.push(target);
                    }
                }
                if (succeeded.length === 0) {
                    setError(localizer("groups.modal.ban.failed"));
                    return;
                }
                onBulkResult?.({ succeeded, failed });
                onSuccess?.();
                onClose?.();
                return;
            }

            await apiClient.banUser(groupPublicId, buildPayload(trimmedUsername));
            if (purgeMessages) {
                try {
                    await ClientFactory.purgeGroupBoardMemberMessages(groupPublicId, trimmedUsername, "LAST_DAY");
                } catch (purgeError) {
                    // The ban landed; a failed cleanup must not read as a failed ban.
                    logger.error("Ban succeeded but the message purge failed", purgeError);
                }
            }
            onSuccess?.();
            onClose?.();
        } catch (err) {
            // minimal feedback
            // eslint-disable-next-line no-console
            logger.error("Failed banning user", err);
            setError(localizer("groups.modal.ban.failed"));
        } finally {
            setSaving(false);
        }
    }

    return (
        <form onSubmit={handleSubmit} className="w-full">
            <div className="flex flex-col gap-4">
                {error ? <ErrorBanner description={error} className="rounded-xl py-3" /> : null}

                {/* Bulk mode shows a read-only summary of the selection rather than an editable field:
                    a username input that cannot actually change the target set would be a lie. */}
                {isBulk ? (
                    <div
                        className="flex flex-col gap-1 rounded-xl border border-border bg-background2/40 px-3 py-2"
                        data-playwright-id="group-ban-bulk-summary"
                    >
                        <span className="text-sm font-medium">
                            {localizer("group.members.manage.bulk.ban.target_count", String(bulkTargets.length))}
                        </span>
                        <span className="text-xs text-muted-foreground break-words" translate="no">
                            {bulkTargets.map((name) => `@${name}`).join(", ")}
                        </span>
                    </div>
                ) : (
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">
                            {localizer("groups.modal.ban.username.label")}
                        </label>
                        <UsernameSuggestionField
                            localizer={localizer}
                            value={usernameStr}
                            onChange={setUsernameStr}
                            placeholder={localizer("groups.modal.ban.user_id_placeholder")}
                            minLength={REQUEST_VALIDATION_LIMITS.username.minLength}
                            maxLength={REQUEST_VALIDATION_LIMITS.username.maxLength}
                            required
                            disabled={saving || !!targetUsername}
                            readOnly={!!targetUsername}
                        />
                    </div>
                )}

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium">
                        {localizer("groups.modal.ban.reason_type.label")}
                    </label>
                    <NativeSelect
                        value={reasonType}
                        onChange={(ev) => setReasonType(ev.target.value as BanReasonType)}
                        disabled={saving}
                    >
                        {REASON_TYPES.map((r) => {
                            const reasonKey = groupBanReasonLocalizationKey(r.value);
                            return (
                                <option key={r.value} value={r.value}>
                                    {reasonKey ? (localizer(reasonKey)) : r.label}
                                </option>
                            );
                        })}
                    </NativeSelect>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium">
                        {localizer("groups.modal.ban.duration.label")}
                    </label>
                    <NativeSelect
                        value={duration}
                        onChange={(ev) => setDuration(ev.target.value as BanDuration)}
                        disabled={saving}
                    >
                        {DURATION_OPTIONS.map((d) => (
                            <option key={d.value} value={d.value}>
                                {d.labelKey ? (localizer(d.labelKey)) : d.labelFallback}
                            </option>
                        ))}
                    </NativeSelect>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium">
                        {localizer("groups.modal.ban.reason.label")}
                    </label>
                    <Textarea
                        value={reason}
                        onChange={(ev) => setReason(ev.target.value)}
                        placeholder={localizer("groups.modal.ban.reason_placeholder")}
                        className="min-h-[80px]"
                        maxLength={REQUEST_VALIDATION_LIMITS.longText.maxLength}
                        required
                        disabled={saving}
                    />
                </div>

                {!isBulk ? (
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={purgeMessages}
                            onChange={(ev) => setPurgeMessages(ev.target.checked)}
                            disabled={saving}
                            data-playwright-id="group-ban-purge-messages"
                        />
                        <span>{localizer("group.chat.moderation.purge_checkbox")}</span>
                    </label>
                ) : null}
            </div>

            <SimpleDialogFooter>
                <CtaButton
                    type="button"
                    onClick={() => onClose?.()}
                    text={localizer("literal.cancel")}
                    extraClass=""
                    icon={X}
                    disabled={saving}
                    variant={"outline"}
                />

                <CtaButton
                    type="button"
                    onClick={() => handleSubmit()}
                    text={localizer("groups.modal.ban.ban_button")}
                    extraClass=""
                    icon={AlertTriangle}
                    disabled={saving}
                    variant={"destructive"}
                />
            </SimpleDialogFooter>
        </form>
    );
}

export function showBanUserDialog(groupPublicId: string, localizer: Localizer, username?: string, onSuccess?: () => void) {
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={<div id="ban-user-modal-title">{localizer("groups.modal.ban.title")}</div>}
            width="md"
        >
            <GroupBanUserDialogInner
                groupPublicId={groupPublicId}
                onClose={() => onOpenChange(false)}
                localizer={localizer}
                targetUsername={username}
                onSuccess={onSuccess}
            />
        </SimpleDialog>
    ));
}

/**
 * Bulk ban (roadmap P2-C). One reason/duration/type is collected once and applied to each selected
 * member through the same per-user endpoint — so the same authorization applies to every target, and
 * no second moderation path exists to keep in sync. The title carries the count because a destructive
 * action over an invisible set is the easy way to ban the wrong people.
 */
export function showBulkBanUsersDialog(
    groupPublicId: string,
    localizer: Localizer,
    usernames: string[],
    onBulkResult?: (result: { succeeded: string[]; failed: string[] }) => void,
) {
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={
                <div id="ban-user-modal-title">
                    {localizer("group.members.manage.bulk.ban.title", String(usernames.length))}
                </div>
            }
            width="md"
        >
            <GroupBanUserDialogInner
                groupPublicId={groupPublicId}
                onClose={() => onOpenChange(false)}
                localizer={localizer}
                targetUsernames={usernames}
                onBulkResult={onBulkResult}
            />
        </SimpleDialog>
    ));
}
